import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TID } from "@/lib/testIds";
import { WaitlistDialog } from "@/components/WaitlistDialog";

const YOUTH_IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_coast2coast-hoops/artifacts/cl9u0pdq_Coach%20Dunking.webp";
const GROUP_IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_coast2coast-hoops/artifacts/8zsgksee_7978dace-ffae-4097-8cd4-090d51a0b20e.JPG";

const PROGRAMS = [
  {
    code: "P/01",
    name: "1-on-1 Development",
    focus: "Skill, footwork, shot mechanics",
    who: "All ages up to 18 · All levels",
    length: "60 min",
    price: "$100",
    priceNote: "per session",
    img: YOUTH_IMG,
    pos: "center 20%",
    long: "Fully tailored private session. We break it down, we correct, we rebuild. Players leave with a personal focus for the week ahead.",
  },
  {
    code: "P/02",
    name: "Small Group (2–8)",
    focus: "Live decisions, competitive reps",
    who: "Groups of 2–8 players",
    length: "10 × 60 min",
    price: "$50",
    priceNote: "per player · per session",
    img: GROUP_IMG,
    pos: "20% 30%",
    long: "Where skills meet the scoreboard. Group training runs as a 10-session deal — $500 per player for groups of 2–4, $400 per player for groups of 5–8. Book the first session and we lock in the rest together.",
  },
  {
    code: "P/03",
    name: "Lock in 10 Sessions",
    focus: "Consistency beats intensity",
    who: "Serious about growth",
    length: "10 × 60 min",
    price: "$900",
    priceNote: "10-pack · save $100",
    img: null,
    kind: "package",
    long: "The package that actually moves the needle. Ten sessions locked in ahead — priority slots, a real plan, and visible change in your player's game. All 10 sessions are to be used within a 12-week period.",
  },
  {
    code: "P/04",
    name: "Holiday Camps",
    focus: "Multi-day skill blocks",
    who: "All ages up to 18",
    length: "Coming 2027",
    price: "TBD",
    priceNote: "announced soon",
    img: null,
    kind: "soon",
    long: "Holiday camps land in 2027 — concentrated multi-day blocks of skill work, competition and games during school breaks. Waitlist opening early.",
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      delay: i * 0.08,
    },
  }),
};

export const Programs = () => {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  return (
  <section
    id="programs"
    data-testid={TID.programs}
    className="relative py-32 md:py-48 px-6 md:px-12"
  >
    <div className="max-w-[1600px] mx-auto">
      <div className="grid grid-cols-12 gap-6 mb-20">
        <div className="col-span-12 md:col-span-4">
          <div className="chapter-num mb-6">Programs</div>
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight"
          >
            Four ways to train.
            <br />
            <span className="text-[#2a5fdb]">One standard.</span>
          </motion.h2>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {PROGRAMS.map((p, i) => (
          <motion.a
            key={p.code}
            href={p.kind === "soon" ? "#waitlist" : "/book"}
            onClick={
              p.kind === "soon"
                ? (e) => {
                    e.preventDefault();
                    setWaitlistOpen(true);
                  }
                : undefined
            }
            data-testid={p.kind === "soon" ? "program-card-camps" : undefined}
            custom={i}
            variants={cardVariant}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-10%" }}
            className="group col-span-12 md:col-span-6 border border-white/10 bg-[#0a1428] hover:bg-[#0d1a35] transition-colors duration-500 flex flex-col overflow-hidden"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-[#060b14]">
              {p.img ? (
                <>
                  <div
                    className="absolute inset-0 bg-cover contrast-105 group-hover:scale-105 transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                    style={{
                      backgroundImage: `url(${p.img})`,
                      backgroundPosition: p.pos || "center",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1428] via-transparent to-transparent" />
                </>
              ) : p.kind === "package" ? (
                <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_center,rgba(42,95,219,0.15),transparent_70%)]">
                  <div className="font-display uppercase text-8xl md:text-9xl text-white leading-none tracking-tighter">
                    10<span className="text-[#2a5fdb]">×</span>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#2a5fdb]">
                    Coming Soon
                  </div>
                  <div className="font-display uppercase text-6xl md:text-7xl text-white/15 leading-none tracking-tighter">
                    2027
                  </div>
                </div>
              )}
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/70">
                {p.code}
              </div>
              <div className="absolute top-4 right-4 text-white/60 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500">
                <ArrowUpRight strokeWidth={1.25} size={22} />
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col gap-5 flex-1">
              <div className="flex items-start justify-between gap-6">
                <h3 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] tracking-tight">
                  {p.name}
                </h3>
                <div className="text-right shrink-0">
                  <div className="font-display text-4xl md:text-5xl leading-none text-[#2a5fdb]">
                    {p.price}
                  </div>
                  <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">
                    {p.priceNote}
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{p.long}</p>
              {p.kind === "soon" && (
                <span
                  data-testid="waitlist-open-btn"
                  className="inline-flex items-center gap-2 self-start border border-[#2a5fdb] text-[#2a5fdb] group-hover:bg-[#2a5fdb] group-hover:text-white transition-colors px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em]"
                >
                  Join the 2027 waitlist <span aria-hidden>→</span>
                </span>
              )}

              <dl className="mt-auto grid grid-cols-1 gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 pt-6 border-t border-white/10">
                <div className="flex justify-between gap-4">
                  <dt>Focus</dt>
                  <dd className="text-white/80 text-right">{p.focus}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Who</dt>
                  <dd className="text-white/80 text-right">{p.who}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Length</dt>
                  <dd className="text-white/80 text-right">{p.length}</dd>
                </div>
              </dl>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
    <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} />
  </section>
  );
};
